import {
  type ContractInteractionChildWithContext,
  isUndecodedContractInteraction,
} from '@mls-wallet/shared-types';
import { AddressDisplay } from '@/components/shared/address-display';
import { OpaqueContractDataDetails } from './opaque-contract-data-details';

function DetailLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-sm font-normal text-muted-foreground">{children}</div>
  );
}

function DetailValue({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`text-sm font-medium ${className}`}>{children}</div>;
}

/**
 * Shows a contract call's target and either decoded parameters or opaque
 * calldata and value when function metadata is unavailable.
 */
export function ContractInteractionDetails({
  transaction,
}: {
  transaction: ContractInteractionChildWithContext;
}) {
  const isOpaqueContractCall = isUndecodedContractInteraction(transaction);
  const hasContractName =
    transaction.contractName &&
    transaction.contractName !== transaction.toAddress;

  return (
    <div className="mb-4 mt-4 space-y-4 border-t px-7 pt-4">
      <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-3">
        {hasContractName ? (
          <>
            <DetailLabel>Contract Name</DetailLabel>
            <DetailValue>{transaction.contractName}</DetailValue>
          </>
        ) : null}

        <DetailLabel>Contract Address</DetailLabel>
        <DetailValue className="text-xs">
          <AddressDisplay
            address={transaction.toAddress}
            name={transaction.contractAddressName}
            networkId={transaction.networkId}
            showNetwork
            showCopy
            showBlockExplorerLink={false}
          />
        </DetailValue>

        {!isOpaqueContractCall ? (
          <>
            <DetailLabel>Function</DetailLabel>
            <DetailValue>{transaction.functionName}</DetailValue>
          </>
        ) : null}
      </div>

      {isOpaqueContractCall ? (
        <OpaqueContractDataDetails
          calldata={transaction.data}
          value={transaction.value}
        />
      ) : (
        <div className="mt-2">
          <DetailLabel>Parameters</DetailLabel>
          <div className="mt-1 rounded-md bg-muted p-2">
            {transaction.functionParameters.map((param, index) => (
              <div key={index} className="mb-1 text-xs">
                <span className="font-medium">{param.name}</span> ({param.type}
                ):
                <span className="ml-1 break-all font-mono">{param.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
